import { createApi } from '@reduxjs/toolkit/query/react';
import { message } from 'antd';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import { runMutationOnce, notifyUnlessUnauthorized } from '@/pages/shared/rtk-query-helpers';

const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Report downloads replace axios' `responseType: 'blob'`. A failed response is read as
 * text instead so the numeric status survives into `error.status`, which both the 401
 * refresh in `baseQueryWithReauth` and `notifyUnlessUnauthorized` depend on.
 */
const blobResponseHandler = async (response) =>
    response.ok ? response.blob() : response.text();

const toReportFilters = ({ siteId, dateFrom, dateTo, category, subcategory }, requireDates) => ({
    siteId,
    from: requireDates ? dateFrom.format(API_DATE_FORMAT) : dateFrom?.format(API_DATE_FORMAT) ?? null,
    to: requireDates ? dateTo.format(API_DATE_FORMAT) : dateTo?.format(API_DATE_FORMAT) ?? null,
    category,
    subcategory,
});

// Only the user access report reads these, but the server expects them to be present.
const toUserStatusFilters = ({ showSuspendedUsers, showDisabledUsers }) => ({
    showSuspendedUsers: showSuspendedUsers || false,
    showDisabledUsers: showDisabledUsers || false,
});

export const reportsApi = createApi({
    reducerPath: 'reportsApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        // Every endpoint is a mutation: results are driven by the caller's filter model
        // and downloads must never be replayed from cache.
        getReportData: builder.mutation({
            query: ({ link, body }) => ({ url: `/api/report/${link}`, method: 'POST', body }),
        }),
        downloadReportByDate: builder.mutation({
            query: ({ link, body }) => ({
                url: `/api/report/${link}/download`,
                method: 'POST',
                body,
                responseHandler: blobResponseHandler,
            }),
        }),
        downloadReportBySite: builder.mutation({
            query: ({ link, body }) => ({
                url: `/api/report/${link}/download`,
                method: 'POST',
                body,
                responseHandler: blobResponseHandler,
            }),
        }),
        downloadReportCsv: builder.mutation({
            query: ({ link, body }) => ({
                url: `/api/report/${link}/downloadcsv`,
                method: 'POST',
                body,
                responseHandler: blobResponseHandler,
            }),
        }),
    }),
});

/**
 * Saves a downloaded blob to disk. `msSaveOrOpenBlob` is a legacy IE/Edge path kept from
 * the axios service rather than dropped as part of a data-layer migration.
 */
export const saveBlob = (blobData, filename) => {
    const fileBlob = new Blob([blobData], { type: 'application/octet-stream' });

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(fileBlob, filename);
        return;
    }

    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.style = 'display: none';
    const objectUrl = URL.createObjectURL(fileBlob);
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
    anchor.remove();
};

const runDownload = async (dispatch, endpointName, arg, filename) => {
    message.loading({ content: 'Download...', key: filename, duration: 0 });

    try {
        const blob = await runMutationOnce(dispatch, reportsApi.endpoints[endpointName], arg);

        saveBlob(blob, filename);
        message.success({ content: 'Documents downloaded!', key: filename, duration: 2 });

        return blob;
    } catch (error) {
        notifyUnlessUnauthorized(error, `Some error can't download`);

        if (error?.status !== 401 && error?.originalStatus !== 401) {
            message.error({ content: `Can't download files`, key: filename, duration: 2 });
        }

        throw error;
    }
};

/**
 * Downloads a report as PDF. Tabs 2-6 are date-ranged and post their filters; the rest
 * are site-only and post the user-status filters the user access report needs.
 */
export const downloadReport = (dispatch, currentReport, keyOfTabs, filters) => {
    const isDateRanged = [2, 3, 4, 5, 6].includes(keyOfTabs);
    const arg = isDateRanged
        ? { link: currentReport.link, body: toReportFilters(filters, true) }
        : { link: currentReport.link, body: { siteId: filters.siteId, ...toUserStatusFilters(filters) } };

    return runDownload(
        dispatch,
        isDateRanged ? 'downloadReportByDate' : 'downloadReportBySite',
        arg,
        currentReport.pdfName
    );
};

/**
 * Downloads a report as CSV. Unlike the PDF path the dates are optional here.
 */
export const downloadReportCsv = (dispatch, currentReport, filters) => {
    return runDownload(
        dispatch,
        'downloadReportCsv',
        { link: currentReport.link, body: { ...toReportFilters(filters, false), ...toUserStatusFilters(filters) } },
        currentReport.csvName
    );
};

/**
 * Loads a report's table page. The reports page builds its own table payload rather than
 * going through the shared `getTableData` thunk, so no `{ data }` envelope is added.
 */
export const runReportDataQuery = (dispatch, link, body) =>
    runMutationOnce(dispatch, reportsApi.endpoints.getReportData, { link, body });

export default reportsApi;
